import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NotificationSetting, NotificationTypeKey, NotificationTypes } from '@klickdata/core/notification';
import { RoleValueType } from '@klickdata/core/user';

@Component({
    selector: 'app-notification-settings',
    templateUrl: './notification-settings.component.html',
    styleUrls: ['./notification-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationSettingsComponent implements OnInit {
    @Input() roleValue: RoleValueType;
    @Input() notificationSettings: NotificationSetting[] = [];
    @Input() excludedTypes: NotificationTypeKey[] = [];
    @Input() showSaveButton: boolean = true;
    @Input() saveButtonText: string = 'Save';
    @Input() loading: boolean = false;

    @Output() settingsChange = new EventEmitter<NotificationSetting[]>();
    @Output() save = new EventEmitter<NotificationSetting[]>();

    notificationForm: FormArray;
    notificationTypes = NotificationTypes;

    constructor(private fb: FormBuilder) { }

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        const formGroups: FormGroup[] = [];

        Object.entries(NotificationTypes)
            .filter(([key, item]) => !this.excludedTypes.includes(key) && item.role_values.includes(this.roleValue))
            .forEach(([key, item]) => {
                // Add parent item
                const parentFormGroup = this.fb.group({
                    type: [key],
                    enabled: [this.notificationSettings?.find((note) => note.type === key)?.enabled ?? false],
                    parentKey: [null],
                });
                formGroups.push(parentFormGroup);

                // Add children if they exist
                if (item.children) {
                    const childFormGroups = Object.entries(item.children)
                        .filter(([, childItem]) => childItem.role_values.includes(this.roleValue))
                        .map(([childKey, childItem]) => {
                            const childFormGroup = this.fb.group({
                                type: [childKey],
                                enabled: [this.notificationSettings?.find((note) => note.type === childKey)?.enabled ?? false],
                                parentKey: [key],
                            });
                            return childFormGroup;
                        });
                    formGroups.push(...childFormGroups);
                    this.setupParentEnabled(parentFormGroup, childFormGroups);
                }
            });

        this.notificationForm = this.fb.array(formGroups);
    }

    getItemLabel(type: string, isChild: boolean): string {
        if (isChild) {
            // Find the parent key for this child
            const childControl = this.notificationForm.controls.find(c =>
                c.get('type')?.value === type && c.get('parentKey')?.value
            );
            const parentKey = childControl?.get('parentKey')?.value;
            return NotificationTypes[parentKey]?.children?.[type]?.label || '';
        }
        return NotificationTypes[type]?.label || '';
    }

    getItemDescription(type: string, isChild: boolean): string {
        if (isChild) {
            // Find the parent key for this child
            const childControl = this.notificationForm.controls.find(c =>
                c.get('type')?.value === type && c.get('parentKey')?.value
            );
            const parentKey = childControl?.get('parentKey')?.value;
            return NotificationTypes[parentKey]?.children?.[type]?.description || '';
        }
        return NotificationTypes[type]?.description || '';
    }

    isChildItem(control: AbstractControl): boolean {
        return !!control.get('parentKey')?.value;
    }

    isVisibleControl(control: AbstractControl): boolean {
        if (!this.isChildItem(control)) return true;
        const parentKey = control.get('parentKey')?.value;
        const parentControl = this.notificationForm.controls.find(c =>
            c.get('type')?.value === parentKey && !c.get('parentKey')?.value
        );

        // Children are only enabled when the parent itself is enabled
        const parentEnabled = parentControl?.get('enabled')?.value ?? false;
        return parentEnabled;
    }

    onToggleChange(control: AbstractControl, enabled: boolean): void {
        control.get('type')?.markAsDirty();

        // If it's a parent with children, update all children
        if (!this.isChildItem(control)) {
            const parentKey = control.get('type')?.value;
            const hasChildren = NotificationTypes[parentKey]?.children;

            if (hasChildren) {
                this.onParentToggle(parentKey, enabled);
            }
        } else {
            const parentKey = control.get('parentKey')?.value;
            // check if all children are disabled
            const children = this.notificationForm.controls.filter(c => c.get('parentKey')?.value === parentKey);
            // set parent enabled state
            const parentControl = this.notificationForm.controls.find(c => c.get('type')?.value === parentKey);
            this.setupParentEnabled(parentControl, children);
        }

        // Emit current settings
        this.emitSettingsChange();
    }

    setupParentEnabled(parentControl: AbstractControl, children: AbstractControl[]): void {
        // check if all children are disabled
        const isParentEnabled = children.some(c => c.get('enabled')?.value === true);
        // set parent enabled state
        parentControl?.get('enabled')?.setValue(isParentEnabled, { emitEvent: false });
        parentControl?.get('enabled')?.markAsDirty();
    }

    onParentToggle(parentKey: string, enabled: boolean): void {
        // Find all children of this parent and set their enabled state
        this.notificationForm.controls
            .filter(control => control.get('parentKey')?.value === parentKey)
            .forEach(child => {
                child.get('enabled')?.setValue(enabled, { emitEvent: false });
                child.get('enabled')?.markAsDirty();
            });
    }

    getVisibleControls(): AbstractControl[] {
        return this.notificationForm.controls.filter(control => {
            if (!this.isChildItem(control)) return true;
            return this.isVisibleControl(control);
        });
    }

    private emitSettingsChange(): void {
        const settings = this.getNotificationSettings();
        this.settingsChange.emit(settings);
    }

    getNotificationSettings(): NotificationSetting[] {
        // Filter to only include:
        // 1. Parents WITHOUT children (standalone notifications)
        // 2. Children (when parent has children, only save children)
        return this.notificationForm.controls.filter(control => {
            if (!(control.dirty || control.touched)) return false;

            const type = control.get('type')?.value;
            const isChild = !!control.get('parentKey')?.value;
            const hasChildren = NotificationTypes[type]?.children;

            // Include if it's a child OR if it's a parent without children
            return isChild || !hasChildren;
        }).map(control => ({
            type: control.get('type')?.value,
            enabled: control.get('enabled')?.value
        }));
    }

    onSave(): void {
        const settings = this.getNotificationSettings();
        this.save.emit(settings);
    }
}
